<template>
	<div class="bg-gray-50 relative flex w-full items-center justify-center overflow-hidden px-4 py-12 min-h-dvh-safe">
		<div class="pointer-events-none absolute inset-0 overflow-hidden">
			<div class="absolute -left-[10%] -top-[10%] h-[50vh] w-[50vh] rounded-full bg-primary/10 blur-3xl"></div>
			<div
				class="absolute -bottom-[10%] -right-[10%] h-[50vh] w-[50vh] rounded-full bg-primary/10 blur-3xl"
			></div>
		</div>
		<div
			class="absolute inset-0 z-0 opacity-25"
			style="background-image: radial-gradient(#000 1px, transparent 1px); background-size: 24px 24px"
		></div>

		<div class="relative z-10 w-full max-w-lg">
			<div
				class="bg-white overflow-hidden rounded-2xl shadow-xl ring-1 ring-gray-900/5 transition-all duration-300 hover:shadow-2xl"
			>
				<div class="flex flex-col items-center px-8 pb-8 pt-12 text-center sm:px-12">
					<div
						class="mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-primary/10 text-primary ring-1 ring-primary/20"
					>
						<Icon
							:name="currentErrorConfig?.icon || 'mdi:alert-circle'"
							class="h-12 w-12 transition-transform duration-500 hover:rotate-6 hover:scale-110"
						/>
					</div>

					<h2 class="text-gray-900 mb-2 text-5xl font-extrabold tracking-tight sm:text-6xl">
						{{ errorCodeDisplay }}
					</h2>

					<h1 class="text-gray-800 mb-4 text-xl font-bold sm:text-2xl">
						{{ errorTitle }}
					</h1>

					<p class="text-gray-500 text-balance text-base leading-relaxed">
						{{ errorDescription }}
					</p>
				</div>

				<div v-if="currentErrorConfig?.showProgress" class="flex justify-center pb-8">
					<div class="flex space-x-2">
						<div class="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]"></div>
						<div class="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]"></div>
						<div class="h-2 w-2 animate-bounce rounded-full bg-primary"></div>
					</div>
				</div>

				<div class="bg-gray-100 h-px w-full"></div>

				<div class="bg-gray-50 px-8 py-6 sm:px-12">
					<div class="flex flex-col gap-3 sm:flex-row sm:justify-center">
						<button
							:class="[
								'group text-white relative inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0f172a] px-6 py-3.5 text-sm font-semibold shadow-sm transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#0f172a] focus:ring-offset-2 sm:w-auto',
								currentErrorConfig?.hideHome ? 'flex-grow' : ''
							]"
							@click="handleError"
						>
							<Icon
								name="mdi:refresh"
								class="h-5 w-5 transition-transform duration-500 group-hover:rotate-180"
							/>
							{{ $t("error_retry") }}
						</button>

						<button
							v-if="!currentErrorConfig?.hideHome"
							class="bg-white text-gray-700 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold shadow-sm ring-1 ring-inset ring-gray-300 transition-all duration-200 hover:text-gray-900 hover:bg-gray-50 sm:w-auto"
							@click="goHome"
						>
							<Icon name="mdi:home-outline" class="text-gray-400 h-5 w-5" />
							{{ $t("error_back_home") }}
						</button>
					</div>

					<p v-if="currentErrorConfig?.showSupport" class="text-gray-400 mt-6 text-center text-xs">
						{{ $t("error_503_support") }}
					</p>
				</div>
			</div>

			<div class="mt-8 text-center">
				<p class="text-gray-400 text-sm">&copy; {{ moment().jYear() }} {{ $t("app_name") }}</p>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { NuxtError } from "#app"

const props = defineProps<{
	error: NuxtError
}>()

const moment = useMoment()

interface ErrorConfig {
	code: number
	icon: string
	showProgress?: boolean
	showSupport?: boolean
	hideHome?: boolean
}

// Keeping your logic exactly as is, just formatting
const errorConfigs: ErrorConfig[] = [
	{ code: 400, icon: "mdi:alert-circle-outline" },
	{ code: 401, icon: "mdi:lock-outline" },
	{ code: 403, icon: "mdi:shield-alert-outline" }, // Updated icon
	{ code: 404, icon: "mdi:map-search-outline" }, // Updated icon
	{ code: 408, icon: "mdi:clock-alert-outline" },
	{ code: 429, icon: "mdi:speedometer-slow" },
	{ code: 500, icon: "mdi:server-network-off" },
	{ code: 502, icon: "mdi:server-off" },
	{ code: 503, icon: "mdi:hammer-screwdriver", showProgress: true, showSupport: true, hideHome: true },
	{ code: 504, icon: "mdi:timer-sand" }
]

const currentErrorConfig = computed(() => {
	return errorConfigs.find((config) => config.code === props.error?.statusCode)
})

const errorTitle = computed(() => {
	const code = props.error?.statusCode
	const key = `error_${code}_title`
	const { t, te } = useI18n()
	return te(key) ? t(key) : t("error_default_title")
})

const errorDescription = computed(() => {
	const code = props.error?.statusCode
	const key = `error_${code}_description`
	const { t, te } = useI18n()
	return props.error?.message || (te(key) ? t(key) : t("error_default_description"))
})

const errorCodeDisplay = computed(() => {
	const code = props.error?.statusCode
	if (!code) return "Error" // Fallback english

	// Convert to Persian numerals
	const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"]
	return code
		.toString()
		.split("")
		.map((digit) => persianDigits[parseInt(digit)] || digit)
		.join("")
})

const handleError = () => {
	clearError({ redirect: "/" })
	if (process.client) {
		// Small delay allows the clearError to register before reload
		setTimeout(() => window.location.reload(), 100)
	}
}

const goHome = () => {
	clearError({ redirect: "/" })
}
</script>

<style scoped>
/* We removed the custom bounce animation CSS because we are 
   using standard Tailwind classes.
   text-balance is a new CSS feature supported by newer browsers
   for better paragraph wrapping.
*/
.text-balance {
	text-wrap: balance;
}
</style>
